import axios from "axios";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Swal from "sweetalert2";

const IconEye = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeSlash = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const schema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Mật khẩu không khớp")
    .required("Vui lòng nhập lại mật khẩu"),
  referral_code: yup.string().optional(),
});

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      phone_number: "",
      password: "",
      password_confirmation: "",
      referral_code: "",
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showReferral, setShowReferral] = useState(false);

  const handleRegister = async (data: {
    username: string;
    phone_number: string;
    password: string;
    password_confirmation: string;
    referral_code?: string;
  }) => {
    if (data.password !== data.password_confirmation) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_API}/auth/register`,
        {
          username: data.username,
          managedByUsername: "superadmin",
          phone: data.phone_number,
          password: String(data.password),
          referralCode: data.referral_code,
        }
      );

      if (res.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Đăng ký thành công",
          text: "Vui lòng đợi ít phút.",
          customClass: {
            popup: "custom-swal",
            title: "custom-title",
            htmlContainer: "custom-text",
          },
        });
        reset();
        onSwitchToLogin();
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Lỗi đăng ký",
        text: `${err.response?.data?.message || "Có lỗi xảy ra, vui lòng thử lại!"}`,
        customClass: {
          popup: "custom-swal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="auth-modal__title">&gt;_ĐĂNG  KÝ_&lt;</h1>
      <form
        className="auth-modal__form"
        onSubmit={handleSubmit(handleRegister)}
        noValidate
      >
        <div className="auth-modal__input-wrapper">
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="auth-modal__input auth-modal__input--no-icon"
                type="text"
                placeholder="Nhập tên tài khoản"
                autoComplete="username"
                maxLength={16}
              />
            )}
          />
        </div>
        {errors.username && (
          <p className="auth-modal__error">{errors.username.message}</p>
        )}

        <div className="auth-modal__input-wrapper">
          <Controller
            name="phone_number"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="auth-modal__input auth-modal__input--no-icon"
                type="tel"
                placeholder="Nhập số điện thoại"
                autoComplete="tel"
                maxLength={10}
              />
            )}
          />
        </div>
        {errors.phone_number && (
          <p className="auth-modal__error">{errors.phone_number.message}</p>
        )}

        <div className="auth-modal__input-wrapper">
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="auth-modal__input"
                type={showPassword ? "text" : "password"}
                placeholder="Nhập mật khẩu"
                autoComplete="new-password"
              />
            )}
          />
          <button
            type="button"
            className="auth-modal__toggle-pwd"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
            aria-label="Hiện/Ẩn mật khẩu"
          >
            {showPassword ? <IconEyeSlash /> : <IconEye />}
          </button>
        </div>
        {errors.password && (
          <p className="auth-modal__error">{errors.password.message}</p>
        )}

        <div className="auth-modal__input-wrapper">
          <Controller
            name="password_confirmation"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="auth-modal__input"
                type={showPasswordConfirm ? "text" : "password"}
                placeholder="Nhập lại mật khẩu"
                autoComplete="new-password"
              />
            )}
          />
          <button
            type="button"
            className="auth-modal__toggle-pwd"
            onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            tabIndex={-1}
            aria-label="Hiện/Ẩn mật khẩu"
          >
            {showPasswordConfirm ? <IconEyeSlash /> : <IconEye />}
          </button>
        </div>
        {errors.password_confirmation && (
          <p className="auth-modal__error">
            {errors.password_confirmation.message}
          </p>
        )}

        <div className="auth-modal__input-wrapper">
          <Controller
            name="referral_code"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="auth-modal__input"
                type={showReferral ? "text" : "password"}
                placeholder="Nhập mã giới thiệu"
              />
            )}
          />
          <button
            type="button"
            className="auth-modal__toggle-pwd"
            onClick={() => setShowReferral(!showReferral)}
            tabIndex={-1}
            aria-label="Hiện/Ẩn mã giới thiệu"
          >
            {showReferral ? <IconEyeSlash /> : <IconEye />}
          </button>
        </div>

        <button
          type="submit"
          className="auth-modal__submit"
          disabled={submitting}
        >
          ĐĂNG KÝ NGAY
        </button>
      </form>

      <p className="auth-modal__footer">
        Đã có tài khoản?{" "}
        <button
          type="button"
          className="auth-modal__link"
          onClick={onSwitchToLogin}
        >
          Đăng nhập ngay
        </button>
      </p>
    </>
  );
};

export default RegisterForm;
